import { createFileRoute, Navigate } from '@tanstack/react-router';
import { useLegacyMode } from '@/entities/legacy-mode';
import { LegacyOrderCreatePage } from '@/pages/legacy-order-create';
import { requireAuth } from '../require-auth';

/**
 * Amend or duplicate an order — LEGACY ONLY.
 *
 * ONE route for both, split by a `copy` search param, exactly as the old panel
 * did (`/delivery/edit/:id` and `/delivery/edit/:id?copy=true`). The two flows
 * share the whole form and differ only in where they submit and which defaults
 * they reset, so splitting them into separate routes would duplicate the
 * screen to express a boolean.
 *
 * Like the create route this does not branch on legacy mode: the new Delivery
 * UI has no edit screen, so with the toggle off it redirects rather than
 * showing a legacy screen to someone who never opted in.
 */
export const Route = createFileRoute('/delivery/orders_/$orderId/edit')({
  beforeLoad: requireAuth,
  validateSearch: (search: Record<string, unknown>): { copy: boolean } => {
    // `?copy=true` — read as a string because that is how the old panel wrote
    // it, and how a pasted URL still arrives.
    const { copy } = search;
    return { copy: copy === true || copy === 'true' };
  },
  component: DeliveryOrderEditRoute,
});

function DeliveryOrderEditRoute() {
  const legacy = useLegacyMode();
  const { orderId } = Route.useParams();
  const { copy } = Route.useSearch();

  if (!legacy) return <Navigate to="/delivery/orders" />;

  const id = Number(orderId);
  // A non-numeric id can only come from a hand-edited URL; the detail query
  // would 404 on it, so bounce rather than render a permanently loading form.
  if (!Number.isFinite(id)) return <Navigate to="/delivery/orders" />;

  return <LegacyOrderCreatePage mode={copy ? 'copy' : 'edit'} orderId={id} />;
}
